import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "../../components/common/PageHeader";
import { StatCard } from "../../components/common/StatCard";
import { StatusBadge } from "../../components/common/StatusBadge";
import { CashSettlementService } from "../../services";
import { currency, dateTime } from "../../utils/format";
import { currentBusinessDate } from "../../utils/businessDay";
import { sriLankaYear } from "../../utils/sriLankaTime";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Clock3,
  HandCoins,
  Loader2,
  RefreshCw,
  Search,
  Store,
  Wallet,
  X,
  XCircle,
} from "lucide-react";

const SECTION = {
  OWNER: "OWNER",
  RESTAURANT: "RESTAURANT",
  HISTORY: "HISTORY",
};

const PAGE_SIZE_OPTIONS = [10, 25, 50];

const todayString = () => currentBusinessDate();

const money = (value) => currency(Number(value || 0));
const toNumber = (value) => Number(value || 0);
const safeDateTime = (value) => (value ? dateTime(value) : "-");

const isSystemStartDate = (value) => {
  if (!value) return true;
  const year = sriLankaYear(value);
  return year != null && year <= 2020;
};

const lastPaymentLabel = (value) => {
  if (isSystemStartDate(value)) return "No previous settlement";
  return safeDateTime(value);
};

const settlementTypeLabel = (type) => {
  if (type === "BAR_OWNER") return "Owner Settlement";
  if (type === "RESTAURANT_CASH") return "Restaurant Settlement";
  return type || "-";
};

const getDialogIcon = (type) => {
  if (type === "success") return CheckCircle2;
  if (type === "error") return XCircle;
  if (type === "warning") return AlertTriangle;
  return Clock3;
};

const getDialogIconClass = (type) => {
  if (type === "success") return "text-emerald-600";
  if (type === "error") return "text-rose-600";
  if (type === "warning") return "text-amber-600";
  return "text-navy-700";
};

const getPaginatedRows = (rows, page, pageSize) => {
  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(page, 1), totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, total);

  return {
    pageRows: rows.slice(startIndex, endIndex),
    total,
    totalPages,
    safePage,
    startDisplay: total === 0 ? 0 : startIndex + 1,
    endDisplay: endIndex,
  };
};

const Dialog = ({ dialog, loading, onClose }) => {
  if (!dialog.open) return null;

  const Icon = getDialogIcon(dialog.type);
  const iconClass = getDialogIconClass(dialog.type);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/50 p-0 sm:items-center sm:p-4">
      <div className="max-h-[92vh] w-full overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:max-w-md sm:rounded-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 p-5">
          <div>
            <h3 className="font-display text-lg font-semibold text-slate-900">
              {dialog.title}
            </h3>
            {dialog.subtitle && (
              <p className="mt-1 text-sm text-slate-500">{dialog.subtitle}</p>
            )}
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-[65vh] overflow-y-auto p-5">
          {dialog.body || (
            <div className="flex items-start gap-3">
              <Icon className={`mt-0.5 h-6 w-6 ${iconClass}`} />
              <p className="text-sm leading-6 text-slate-700">
                {dialog.message}
              </p>
            </div>
          )}
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-slate-100 p-5 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {dialog.cancelLabel || "Close"}
          </button>

          {dialog.onConfirm && (
            <button
              type="button"
              onClick={dialog.onConfirm}
              disabled={loading}
              className={`inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300 ${
                dialog.danger
                  ? "bg-rose-600 hover:bg-rose-700"
                  : "bg-navy-800 hover:bg-navy-900"
              }`}
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {dialog.confirmLabel || "Confirm"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const SectionButton = ({ active, icon: Icon, children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex shrink-0 items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold transition ${
      active
        ? "bms-btn-accent"
        : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
    }`}
  >
    <Icon className="h-4 w-4" />
    {children}
  </button>
);

const MiniMetricCard = ({ label, value, helper, tone = "default" }) => {
  const toneClassName =
    tone === "gold"
      ? "border-gold-200 bg-gold-50"
      : tone === "emerald"
      ? "border-emerald-200 bg-emerald-50"
      : tone === "rose"
      ? "border-rose-200 bg-rose-50"
      : "border-slate-200 bg-slate-50";

  return (
    <div className={`rounded-xl border p-4 ${toneClassName}`}>
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </p>
      <p className="mt-2 font-display text-2xl font-semibold text-slate-950">
        {value}
      </p>
      {helper && <p className="mt-1 text-xs leading-5 text-slate-500">{helper}</p>}
    </div>
  );
};

const DetailTile = ({ label, value, helper }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4">
    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
      {label}
    </p>
    <p className="mt-1 font-display text-sm font-semibold text-slate-900">
      {value}
    </p>
    {helper && <p className="mt-1 text-xs leading-5 text-slate-500">{helper}</p>}
  </div>
);

const RequestPanel = ({ note, setNote, buttonLabel, loading, disabled, disabledMessage, onPay }) => (
  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
    <label className="text-sm font-medium text-slate-700">Note optional</label>
    <textarea
      value={note}
      onChange={(event) => setNote(event.target.value)}
      rows={4}
      className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-navy-500 focus:ring-2 focus:ring-navy-100"
      placeholder="Add note"
    />

    <button
      type="button"
      onClick={onPay}
      disabled={disabled}
      className="bms-btn-accent mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm disabled:cursor-not-allowed disabled:bg-slate-300"
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <HandCoins className="h-4 w-4" />}
      {loading ? "Processing..." : buttonLabel}
    </button>

    {disabledMessage && (
      <p className="mt-3 rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-600">
        {disabledMessage}
      </p>
    )}
  </div>
);

const OwnerSection = ({ bucket, note, setNote, loading, disabled, onPay }) => {
  const payable = toNumber(bucket?.calculatedAmount);
  const hasPendingPreviousRequest = Boolean(bucket?.existingSettlementStatus === "PENDING");

  return (
    <div className="bms-card p-5">
      <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h3 className="font-display text-lg font-semibold">Owner Settlement</h3>
          <p className="mt-1 text-sm leading-6 text-slate-500">
            Create a request for the new bar cash amount available since the last owner settlement request.
          </p>
        </div>

        {hasPendingPreviousRequest && <StatusBadge value="PENDING PREVIOUS REQUEST" />}
      </div>

      <div className="mt-5 grid grid-cols-1 gap-4 xl:grid-cols-[1fr_340px]">
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <MiniMetricCard
              label="New Bar Cash"
              value={money(bucket?.cashAmount)}
              helper="Cash collected after the last covered settlement time"
              tone="emerald"
            />
            <MiniMetricCard
              label="Bar Expenses"
              value={money(bucket?.expenseAmount)}
              helper="Deducted only for this new period"
              tone="rose"
            />
            <MiniMetricCard
              label="Available to Request"
              value={money(payable)}
              helper="Create another request when this amount is greater than zero"
              tone="gold"
            />
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <DetailTile
              label="Calculated From"
              value={lastPaymentLabel(bucket?.periodStart)}
              helper="Previous pending or confirmed owner request coverage ends here"
            />
            <DetailTile
              label="Calculated Up To"
              value={safeDateTime(bucket?.periodEnd)}
              helper="Current calculation end time"
            />
          </div>
        </div>

        <RequestPanel
          note={note}
          setNote={setNote}
          buttonLabel="Create Owner Settlement"
          loading={loading}
          disabled={disabled || payable <= 0}
          disabledMessage={
            payable <= 0
              ? "No new owner payable amount is available right now."
              : null
          }
          onPay={onPay}
        />
      </div>
    </div>
  );
};

const RestaurantSection = ({ bucket, note, setNote, loading, disabled, onPay }) => {
  const payable = toNumber(bucket?.calculatedAmount);
  const hasPendingPreviousRequest = Boolean(bucket?.existingSettlementStatus === "PENDING");

  return (
    <div className="bms-card p-5">
      <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h3 className="font-display text-lg font-semibold">Restaurant Settlement</h3>
          <p className="mt-1 text-sm leading-6 text-slate-500">
            Create a request only for new restaurant cash collected after the last restaurant settlement request.
          </p>
        </div>

        {hasPendingPreviousRequest && <StatusBadge value="PENDING PREVIOUS REQUEST" />}
      </div>

      <div className="mt-5 grid grid-cols-1 gap-4 xl:grid-cols-[1fr_340px]">
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <MiniMetricCard
              label="New Restaurant Cash"
              value={money(bucket?.cashAmount)}
              helper="Cash collected after the last covered restaurant request"
              tone="emerald"
            />
            <MiniMetricCard
              label="Available to Request"
              value={money(payable)}
              helper="Amount to hand over for the new period"
              tone="gold"
            />
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <DetailTile
              label="Business Date"
              value={bucket?.businessDate || "-"}
              helper="Current business day"
            />
            <DetailTile
              label="Calculated From"
              value={safeDateTime(bucket?.periodStart)}
              helper="Last restaurant request coverage ends here"
            />
            <DetailTile
              label="Calculated Up To"
              value={safeDateTime(bucket?.periodEnd)}
              helper="Current calculation end time"
            />
          </div>
        </div>

        <RequestPanel
          note={note}
          setNote={setNote}
          buttonLabel="Create Restaurant Settlement"
          loading={loading}
          disabled={disabled || payable <= 0}
          disabledMessage={
            payable <= 0
              ? "No new restaurant cash is available right now. New restaurant cash will appear here after another cash sale."
              : null
          }
          onPay={onPay}
        />
      </div>
    </div>
  );
};

const PaginationFooter = ({ page, pageSize, total, totalPages, startDisplay, endDisplay, onPageChange, onPageSizeChange }) => {
  const isFirstPage = page <= 1;
  const isLastPage = page >= totalPages;

  return (
    <div className="flex flex-col gap-3 border-t border-slate-100 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
        <span>Rows per page</span>
        <select
          value={pageSize}
          onChange={(event) => onPageSizeChange(Number(event.target.value))}
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none"
        >
          {PAGE_SIZE_OPTIONS.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
        <span>{startDisplay}–{endDisplay} of {total}</span>
      </div>

      <div className="flex items-center justify-end gap-2 text-sm">
        <button type="button" onClick={() => onPageChange(1)} disabled={isFirstPage} className="rounded-lg border border-slate-200 bg-white p-2 text-slate-500 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40" aria-label="First page">
          <ChevronsLeft className="h-4 w-4" />
        </button>
        <button type="button" onClick={() => onPageChange(page - 1)} disabled={isFirstPage} className="rounded-lg border border-slate-200 bg-white p-2 text-slate-500 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40" aria-label="Previous page">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="min-w-[88px] text-center font-medium text-slate-700">Page {page} / {totalPages}</span>
        <button type="button" onClick={() => onPageChange(page + 1)} disabled={isLastPage} className="rounded-lg border border-slate-200 bg-white p-2 text-slate-500 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40" aria-label="Next page">
          <ChevronRight className="h-4 w-4" />
        </button>
        <button type="button" onClick={() => onPageChange(totalPages)} disabled={isLastPage} className="rounded-lg border border-slate-200 bg-white p-2 text-slate-500 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40" aria-label="Last page">
          <ChevronsRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

const PaymentHistorySection = ({ filters, setFilters, paginated, onApply }) => (
  <div className="bms-card p-5">
    <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
      <div>
        <h3 className="font-display text-lg font-semibold">Payment History</h3>
        <p className="mt-1 text-sm text-slate-500">Settlement requests and their current status.</p>
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-[140px_140px_170px_170px_220px_auto]">
        <input type="date" value={filters.fromDate} onChange={(event) => setFilters((prev) => ({ ...prev, fromDate: event.target.value }))} className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none" />
        <input type="date" value={filters.toDate} onChange={(event) => setFilters((prev) => ({ ...prev, toDate: event.target.value }))} className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none" />
        <select value={filters.type} onChange={(event) => setFilters((prev) => ({ ...prev, type: event.target.value }))} className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none">
          <option value="ALL">All Types</option>
          <option value="BAR_OWNER">Owner Settlement</option>
          <option value="RESTAURANT_CASH">Restaurant Settlement</option>
        </select>
        <select value={filters.status} onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))} className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none">
          <option value="ALL">All Status</option>
          <option value="PENDING">Pending</option>
          <option value="CONFIRMED">Confirmed</option>
          <option value="REJECTED">Rejected</option>
          <option value="REVERSED">Reversed</option>
        </select>
        <div className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2">
          <Search className="h-4 w-4 text-slate-400" />
          <input type="text" value={filters.search} onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))} className="w-full bg-transparent text-sm outline-none" placeholder="Search" />
        </div>
        <button type="button" onClick={onApply} className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium hover:bg-slate-50">Apply</button>
      </div>
    </div>

    <div className="mt-5 overflow-hidden rounded-xl border border-slate-200">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-100 text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-slate-500">Requested</th>
              <th className="px-4 py-3 text-left font-medium text-slate-500">Type</th>
              <th className="px-4 py-3 text-left font-medium text-slate-500">Period</th>
              <th className="px-4 py-3 text-right font-medium text-slate-500">Amount</th>
              <th className="px-4 py-3 text-left font-medium text-slate-500">Status</th>
              <th className="px-4 py-3 text-left font-medium text-slate-500">Note</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {paginated.rows.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">No payment history found.</td></tr>
            ) : (
              paginated.rows.map((row) => (
                <tr key={row.settlementId} className="hover:bg-slate-50/70">
                  <td className="px-4 py-3 text-slate-700">{safeDateTime(row.requestedAt)}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{settlementTypeLabel(row.settlementType)}</td>
                  <td className="px-4 py-3 text-slate-700">{safeDateTime(row.periodStart)} → {safeDateTime(row.periodEnd)}</td>
                  <td className="px-4 py-3 text-right font-semibold">{money(row.paidAmount)}</td>
                  <td className="px-4 py-3"><StatusBadge value={row.status} /></td>
                  <td className="px-4 py-3 text-slate-600">{row.cashierNote || row.adminNote || row.reverseReason || "-"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <PaginationFooter
        page={paginated.page}
        pageSize={paginated.pageSize}
        total={paginated.total}
        totalPages={paginated.totalPages}
        startDisplay={paginated.startDisplay}
        endDisplay={paginated.endDisplay}
        onPageChange={paginated.setPage}
        onPageSizeChange={paginated.setPageSize}
      />
    </div>
  </div>
);

export const CashierOwnerSettlementPage = () => {
  const [searchParams] = useSearchParams();
  const selectedBusinessDate = searchParams.get("businessDate") || todayString();
  const routeSection = searchParams.get("section");
  const [activeSection, setActiveSection] = useState(() => searchParams.get("section") === "history" ? SECTION.HISTORY : SECTION.OWNER);
  const [summary, setSummary] = useState(null);
  const [settlementHistory, setSettlementHistory] = useState([]);

  const [historyFilters, setHistoryFilters] = useState({
    fromDate: todayString(),
    toDate: todayString(),
    type: "ALL",
    status: "ALL",
    search: "",
  });

  const [barNote, setBarNote] = useState("");
  const [restaurantNote, setRestaurantNote] = useState("");
  const [paymentPage, setPaymentPage] = useState(1);
  const [paymentPageSize, setPaymentPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [actionLoadingKey, setActionLoadingKey] = useState("");
  const [dialog, setDialog] = useState({ open: false });

  const barBucket = summary?.barOwnerSettlement;
  const restaurantBucket = summary?.restaurantCashSettlement;

  const closeDialog = () => setDialog({ open: false });

  const showMessage = (title, message, type = "info") => {
    setDialog({ open: true, title, message, type, cancelLabel: "OK" });
  };

  const loadSummary = useCallback(async () => {
    const data = await CashSettlementService.current(selectedBusinessDate);
    setSummary(data);
  }, [selectedBusinessDate]);

  const loadSettlementHistory = useCallback(async () => {
    const data = await CashSettlementService.history({ fromDate: historyFilters.fromDate, toDate: historyFilters.toDate, businessDate: selectedBusinessDate });
    setSettlementHistory(data || []);
  }, [historyFilters.fromDate, historyFilters.toDate, selectedBusinessDate]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([loadSummary(), loadSettlementHistory()]);
    } catch (error) {
      showMessage(
        "Unable to load settlement details",
        error?.response?.data?.message || error?.message || "Please refresh and try again.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [loadSummary, loadSettlementHistory]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    setActiveSection(routeSection === "history" ? SECTION.HISTORY : SECTION.OWNER);
  }, [routeSection]);

  const filteredSettlementHistory = useMemo(() => {
    const search = historyFilters.search.trim().toLowerCase();
    return (settlementHistory || []).filter((row) => {
      const matchesType = historyFilters.type === "ALL" || row.settlementType === historyFilters.type;
      const matchesStatus = historyFilters.status === "ALL" || row.status === historyFilters.status;
      const searchable = [row.settlementType, row.status, row.businessDate, row.cashierNote, row.adminNote, row.reverseReason, row.paidAmount]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return matchesType && matchesStatus && (search === "" || searchable.includes(search));
    });
  }, [settlementHistory, historyFilters]);

  const pendingSettlementCount = useMemo(() => {
    return (settlementHistory || []).filter((row) => row.status === "PENDING").length;
  }, [settlementHistory]);

  const paymentPagination = getPaginatedRows(filteredSettlementHistory, paymentPage, paymentPageSize);

  useEffect(() => {
    if (paymentPage > paymentPagination.totalPages) {
      setPaymentPage(paymentPagination.totalPages);
    }
  }, [paymentPage, paymentPagination.totalPages]);

  useEffect(() => {
    setPaymentPage(1);
  }, [historyFilters.type, historyFilters.status, historyFilters.search]);

  const requestSettlement = (settlementType, bucket, note) => {
    if (!bucket) return;

    const amount = toNumber(bucket.calculatedAmount);

    if (amount <= 0) {
      showMessage("No payable amount", "There is no new amount available for this settlement.", "warning");
      return;
    }

    setDialog({
      open: true,
      title: settlementType === "BAR_OWNER" ? "Create Owner Settlement" : "Create Restaurant Settlement",
      subtitle: "This creates a pending request for the amount currently available.",
      body: (
        <div className="space-y-3">
          <div className="rounded-lg bg-slate-50 p-4">
            <div className="text-sm text-slate-500">Request amount</div>
            <div className="mt-1 font-display text-2xl font-semibold">{money(amount)}</div>
          </div>
          <div className="text-sm text-slate-600">
            Period: {safeDateTime(bucket.periodStart)} → {safeDateTime(bucket.periodEnd)}
          </div>
        </div>
      ),
      confirmLabel: "Create Request",
      cancelLabel: "Cancel",
      onConfirm: async () => {
        setActionLoadingKey(settlementType);
        try {
          await CashSettlementService.requestSettlement({
            settlementType,
            businessDate: settlementType === "RESTAURANT_CASH" ? bucket.businessDate : null,
            paidAmount: amount,
            cashierNote: note?.trim() || null,
          });

          if (settlementType === "BAR_OWNER") setBarNote("");
          else setRestaurantNote("");

          closeDialog();
          showMessage("Settlement request created", "The request was saved as pending.", "success");
          await loadAll();
        } catch (error) {
          showMessage(
            "Settlement request failed",
            error?.response?.data?.message || error?.message || "Could not create settlement request.",
            "error"
          );
        } finally {
          setActionLoadingKey("");
        }
      },
    });
  };

  return (
    <div>
      <PageHeader
        title="Settlements"
        subtitle="Create settlement requests for new amounts and review payment history."
        actions={
          <button
            type="button"
            onClick={loadAll}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-5 py-2.5 text-sm hover:bg-slate-50 disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Refresh
          </button>
        }
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Owner Payable" value={loading ? "…" : money(barBucket?.calculatedAmount)} icon="Wallet" tone="navy" />
        <StatCard label="Restaurant Cash" value={loading ? "…" : money(restaurantBucket?.calculatedAmount)} icon="Store" tone="emerald" />
        <StatCard label="Pending Requests" value={loading ? "…" : pendingSettlementCount} icon="Clock" tone="gold" />
        <StatCard label="Business Date" value={loading ? "…" : restaurantBucket?.businessDate || todayString()} icon="Calendar" tone="sky" />
      </div>

      <div className="mt-6 flex gap-2 overflow-x-auto pb-1">
        <SectionButton active={activeSection === SECTION.OWNER} icon={Wallet} onClick={() => setActiveSection(SECTION.OWNER)}>Owner Settlement</SectionButton>
        <SectionButton active={activeSection === SECTION.RESTAURANT} icon={Store} onClick={() => setActiveSection(SECTION.RESTAURANT)}>Restaurant Settlement</SectionButton>
        <SectionButton active={activeSection === SECTION.HISTORY} icon={Clock3} onClick={() => setActiveSection(SECTION.HISTORY)}>Payment History</SectionButton>
      </div>

      <div className="mt-6">
        {activeSection === SECTION.OWNER && (
          <OwnerSection
            bucket={barBucket}
            note={barNote}
            setNote={setBarNote}
            loading={actionLoadingKey === "BAR_OWNER"}
            disabled={loading || actionLoadingKey !== ""}
            onPay={() => requestSettlement("BAR_OWNER", barBucket, barNote)}
          />
        )}

        {activeSection === SECTION.RESTAURANT && (
          <RestaurantSection
            bucket={restaurantBucket}
            note={restaurantNote}
            setNote={setRestaurantNote}
            loading={actionLoadingKey === "RESTAURANT_CASH"}
            disabled={loading || actionLoadingKey !== ""}
            onPay={() => requestSettlement("RESTAURANT_CASH", restaurantBucket, restaurantNote)}
          />
        )}

        {activeSection === SECTION.HISTORY && (
          <PaymentHistorySection
            filters={historyFilters}
            setFilters={setHistoryFilters}
            paginated={{
              rows: paymentPagination.pageRows,
              page: paymentPagination.safePage,
              pageSize: paymentPageSize,
              total: paymentPagination.total,
              totalPages: paymentPagination.totalPages,
              startDisplay: paymentPagination.startDisplay,
              endDisplay: paymentPagination.endDisplay,
              setPage: setPaymentPage,
              setPageSize: (value) => {
                setPaymentPageSize(value);
                setPaymentPage(1);
              },
            }}
            onApply={() => {
              setPaymentPage(1);
              loadSettlementHistory();
            }}
          />
        )}
      </div>

      <Dialog dialog={dialog} loading={Boolean(actionLoadingKey)} onClose={closeDialog} />
    </div>
  );
};
